import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { useLanguage, useRenameUnit, useStats, useUnits } from '../api/hooks'
import type { Unit } from '../api/types'
import { Badge, Button, Card, EmptyState, ErrorText, Input, Spinner } from '../components/ui'

const MAX_CROWNS = 5

export default function LearnPath() {
  const languageId = Number(useParams().id)
  const { data: language } = useLanguage(languageId)
  const { data: units, isPending, error } = useUnits(languageId)
  const { data: stats } = useStats(languageId)

  if (isPending) return <Spinner label="Loading your path" />
  if (error) return <ErrorText error={error} />

  if (!units || units.length === 0) {
    return (
      <EmptyState
        title="No words yet"
        hint="A lesson path builds itself out of your vocabulary, six words at a time. Add some words and it will appear here."
        action={
          <Link to={`/languages/${languageId}`}>
            <Button>Add words</Button>
          </Link>
        }
      />
    )
  }

  const totalCrowns = units.reduce((sum, unit) => sum + unit.crowns, 0)
  const possible = units.length * MAX_CROWNS

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <Link
            to={`/languages/${languageId}`}
            className="text-sm text-muted hover:text-ink"
          >
            ← {language?.name}
          </Link>
          <h1 className="text-2xl font-semibold tracking-tight">Learn</h1>
          <p className="text-sm text-muted">
            {totalCrowns} of {possible} crowns
            {stats ? ` · ${stats.learned} words learned` : ''}
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Link to={`/languages/${languageId}/games`}>
            <Button variant="secondary">Games</Button>
          </Link>
          {language?.pronunciation_locale && (
            <Link to={`/languages/${languageId}/sing`}>
              <Button variant="secondary">Sing along</Button>
            </Link>
          )}
          <Link to={`/languages/${languageId}/practice`}>
            <Button variant="secondary">Review</Button>
          </Link>
        </div>
      </div>

      <ol className="relative space-y-3">
        {units.map((unit) => (
          <li key={unit.unit_index}>
            <UnitRow
              unit={unit}
              languageId={languageId}
              editable={language?.is_owner ?? false}
            />
          </li>
        ))}
      </ol>
    </div>
  )
}

function UnitRow({
  unit,
  languageId,
  editable,
}: {
  unit: Unit
  languageId: number
  editable: boolean
}) {
  const rename = useRenameUnit(languageId)
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState(unit.name)

  const done = unit.crowns >= MAX_CROWNS

  return (
    <Card
      className={`transition ${
        unit.unlocked ? 'hover:border-brand' : 'opacity-60'
      }`}
    >
      <div className="flex flex-wrap items-center gap-4">
        {/* Crown ring: the unit's progress at a glance. */}
        <div
          className={`grid size-14 shrink-0 place-items-center rounded-full text-xl ${
            done
              ? 'bg-brand text-white'
              : unit.unlocked
                ? 'bg-brand-soft text-brand'
                : 'bg-canvas text-muted'
          }`}
        >
          {unit.unlocked ? (done ? '★' : unit.unit_index + 1) : '🔒'}
        </div>

        <div className="min-w-40 flex-1">
          {editing ? (
            <form
              onSubmit={(event) => {
                event.preventDefault()
                rename.mutate(
                  { unitIndex: unit.unit_index, name },
                  { onSuccess: () => setEditing(false) },
                )
              }}
              className="flex gap-2"
            >
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoFocus
                maxLength={80}
                onKeyDown={(e) => e.key === 'Escape' && setEditing(false)}
              />
              <Button type="submit" disabled={rename.isPending}>
                Save
              </Button>
            </form>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <h2 className="font-medium">{unit.name}</h2>
                {editable && (
                  <button
                    onClick={() => setEditing(true)}
                    className="text-xs text-muted hover:text-ink hover:underline"
                  >
                    rename
                  </button>
                )}
              </div>
              <p className="text-sm text-muted">
                {unit.preview.join(' · ')}
                {unit.entry_count > unit.preview.length && ' …'}
              </p>
            </>
          )}

          <div className="mt-1.5 flex items-center gap-1.5">
            {Array.from({ length: MAX_CROWNS }, (_, i) => (
              <span
                key={i}
                className={`text-sm ${i < unit.crowns ? '' : 'opacity-20'}`}
                aria-hidden
              >
                👑
              </span>
            ))}
            <span className="ml-1 text-xs text-muted">
              {unit.entry_count} {unit.entry_count === 1 ? 'word' : 'words'}
            </span>
          </div>
        </div>

        <div className="shrink-0">
          {unit.unlocked ? (
            <Link to={`/languages/${languageId}/lesson/${unit.unit_index}`}>
              <Button variant={unit.crowns === 0 ? 'primary' : 'secondary'}>
                {unit.crowns === 0 ? 'Start' : 'Practise'}
              </Button>
            </Link>
          ) : (
            <Badge>Locked</Badge>
          )}
        </div>
      </div>
    </Card>
  )
}
